import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, vShow as _vShow, createVNode as _createVNode, withDirectives as _withDirectives, renderSlot as _renderSlot, resolveDynamicComponent as _resolveDynamicComponent, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementBlock as _createElementBlock, Fragment as _Fragment } from "vue"

const _hoisted_1 = ["innerHTML"]

import UnloggedAreaLayout from '@/components/unloggedArea/UnloggedAreaLayout.vue';
import UserAreaLayout from '@/components/userArea/UserAreaLayout.vue';
import { computed, nextTick, onBeforeMount, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useAppStore } from '@/stores/app.store';
import AppLoader from '@/components/common/loaders/LoaderApp.vue';
import { useRoute } from 'vue-router';
import { layoutAreaTypes } from '@/types/layoutTypes';
import helpers from '@/helpers';
import useAccessibility from '@/composables/useAccessibility';
import { useSmoothAnchors } from '@/composables/useSmoothAnchors';
import { useHotjar } from '@/composables/useHotjar';
import { onErrorCaptured } from 'vue';
import { updateGlobalOptions } from 'vue3-toastify';
import ToastHost from '@/components/common/toast/ToastHost.vue';
import useDelegationToast from '@/composables/useDelegationToast';
import { isString } from 'lodash';
import { usePostHog } from '@/composables/usePostHog';


export default /*@__PURE__*/_defineComponent({
  __name: 'App',
  setup(__props) {

const appStore = useAppStore();
const route = useRoute();
const isTouchDevice = ref(false);

// Initialize accessibility and delegation toast behavior
useAccessibility();
useSmoothAnchors();
useHotjar();
useDelegationToast();
usePostHog();

const componentLayout = computed(() => {
  switch (route?.meta.layout) {
    case layoutAreaTypes.User:
      return UserAreaLayout;
    case layoutAreaTypes.Unlogged:
      return UnloggedAreaLayout;
    default:
      return layoutAreaTypes.Unlogged;
  }
});

const checkTouchDevice = () => {
  isTouchDevice.value = window.matchMedia('(hover: none) and (pointer: coarse)').matches;
};

const onResize = () => {
  checkTouchDevice();
  appStore.isMobile = window.innerWidth <= appStore.mobileBreakpoint;
  if (appStore.isMobile) {
    document?.body?.classList?.toggle('mobile-layout', appStore.isMobile);
    document?.documentElement?.style?.setProperty(
      '--mobile-breakpoint',
      `${appStore.mobileBreakpoint}px`,
    );
  }
};

onBeforeMount(async () => {
  // Configure vue3-toastify to show newest toasts on top
  // This must be called before any toasts are created
  updateGlobalOptions({
    newestOnTop: true,
    clearOnUrlChange: false,
  });

  await appStore.fetchCultureSettings();
  await appStore.fetchCustomCssSettings();
  await Promise.all([appStore.fetchSystemSettings(), appStore.fetchConfiguration()]);
});

onMounted(() => {
  onResize();
  window.addEventListener('resize', onResize);
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', onResize);
});

watch(
  () => appStore.cultureCode,
  async (newCultureCode) => {
    if (newCultureCode) {
      appStore.dir = helpers.lang.getLangDirectionByLangLocaleCode(appStore.cultureCode);
      helpers.lang.setLagAndDirOnHtmlByLangCode(appStore.cultureCode);
      await appStore.fetchTranslations();
    }
  },
  { deep: true },
);

watch(
  () => appStore.isAppDataFetching,
  async (isAppDataFetching) => {
    if (!isAppDataFetching) {
      await nextTick();
      window.dispatchEvent(new CustomEvent('is-app-data-fetched'));
    }
  },
  { deep: true },
);

// Workaround for Kendo Editor cleanup bug
// The KendoEditor tries to set read-only properties during unmount
// This catches and suppresses that specific error to prevent console warnings
onErrorCaptured((err: Error) => {
  if (
    err instanceof TypeError &&
    err.message.includes('Cannot set property props') &&
    err.message.includes('which has only a getter')
  ) {
    // Suppress the error - this is a known Kendo Editor bug in v7.0.0
    // The cleanup still completes successfully despite this error
    return false;
  }
  // Let other errors propagate
  return true;
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _withDirectives(_createVNode(AppLoader, null, null, 512), [
      [_vShow, !_unref(appStore).isAppReady]
    ]),
    _createVNode(ToastHost),
    (_unref(route)?.meta?.layout)
      ? _withDirectives((_openBlock(), _createBlock(_resolveDynamicComponent(componentLayout.value), { key: 0 }, {
          default: _withCtx(() => [
            _renderSlot(_ctx.$slots, "default")
          ]),
          _: 3
        }, 512)), [
          [_vShow, _unref(appStore).isAppReady]
        ])
      : _createCommentVNode("", true),
    (_unref(isString)(_unref(appStore)?.customCssSettings))
      ? (_openBlock(), _createElementBlock("span", {
          key: 1,
          class: "custom-css-wrapper",
          innerHTML: _unref(appStore)?.customCssSettings
        }, null, 8, _hoisted_1))
      : _createCommentVNode("", true)
  ], 64))
}
}

})